import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome'
import { useRouter } from 'expo-router'
import React from 'react'
import { Dimensions, FlatList, Text, TouchableOpacity, View } from 'react-native'
import { ms, ScaledSheet } from 'react-native-size-matters'
import { useSelector } from 'react-redux'
import IconCreateAChatAltPink from '~components/IconCreateAChatAltPink'
import ModalScreen, { getModalStyleDefinition } from '~components/ModalScreen'
import { isSmallDevice } from '~constants/Layout'
import { ApplicationState } from '~redux'
import { useTheme } from '~theme/ThemeManager'
import { actions, Source, useNewChatContext } from './state'

type Method = { source: Source; name: string; icon: [IconPrefix, IconName] | React.JSX.Element }

export default function NewMethod() {
  const router = useRouter()

  const { theme } = useTheme()
  const styles = useStyles()
  const [state, stateDispatch] = useNewChatContext()
  const customer_id = useSelector((state: ApplicationState) => state.auth.user?.claims.customer_id)

  const dismiss = React.useCallback(() => {
    stateDispatch(actions.reset())
  }, [stateDispatch])

  const sources: Method[] = React.useMemo(() => {
    const options: Method[] = [
      {
        source: 'empty',
        name: 'Empty',
        icon: (
          <IconCreateAChatAltPink
            style={styles.sourceIcon}
            width={ms(24, 0.9)}
            height={ms(24, 0.9)}
          />
        ),
      },
      {
        source: 'community',
        name: 'Community',
        icon: ['fal', 'hands-heart'],
      },
    ]

    if (customer_id) {
      options.push({
        source: 'school',
        name: 'School',
        icon: ['fal', 'school'],
      })
    }

    return options
  }, [customer_id, styles.sourceIcon])

  return (
    <ModalScreen onDismiss={dismiss} dismissTo={state.dismissTo}>
      <View style={styles.inner}>
        <View style={styles.content}>
          {/* {progress(0)} */}

          <FlatList
            contentContainerStyle={[styles.content]}
            scrollEnabled={false}
            numColumns={isSmallDevice ? 1 : sources.length}
            columnWrapperStyle={isSmallDevice ? undefined : styles.column}
            data={sources}
            ItemSeparatorComponent={() => <View style={styles.listVerticalSeparator} />}
            ListHeaderComponent={() => (
              <View style={styles.titleContainer}>
                <Text style={styles.title}>New Chat</Text>
                <Text style={styles.subtitle}>
                  Do you want to create an empty chat or use a template?
                </Text>
              </View>
            )}
            renderItem={({ item }) => (
              <TouchableOpacity
                style={[
                  styles.sourceContainer,
                  item.source === state.source && styles.listItemSelected,
                ]}
                onPress={() => {
                  router.push(
                    state.showFolderSelection
                      ? `/folders/new-chat/${item.source}`
                      : `/folders/${state.class.id}/new-chat/${item.source}`,
                    {
                      withAnchor: true,
                    },
                  )
                }}
              >
                {React.isValidElement(item.icon) ? (
                  item.icon
                ) : (
                  <FontAwesomeIcon
                    color={theme.primary}
                    style={styles.sourceIcon}
                    size={ms(24, 0.9)}
                    icon={item.icon}
                  />
                )}
                <Text style={[styles.sourceText]}>{item.name}</Text>
              </TouchableOpacity>
            )}
          />
        </View>
      </View>
    </ModalScreen>
  )
}

const useStyles = () => {
  const { theme, mode } = useTheme()
  return ScaledSheet.create({
    ...getModalStyleDefinition(theme, mode),
    listItem: {
      marginVertical: '5@mvs',
      padding: '5@ms',
      backgroundColor: theme.white,
    },
    listItemSelected: {
      backgroundColor: theme.pink[200],
    },
    layoutsContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },
    backgroundContainerStyle: {
      borderColor: theme.primary,
    },
    textContainerStyle: {
      borderColor: theme.primary,
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignContent: 'center',
      alignItems: 'center',
      paddingTop: '10@mvs0.8',
      paddingStart: '15@ms',
      paddingHorizontal: '10@mvs0.8',
    },
    folderInput: {
      color: theme.primary,
    },
    sourceContainer: {
      backgroundColor: theme.white,
      borderWidth: 1,
      borderColor: theme[300],
      borderRadius: '5@ms',
      marginHorizontal: '10@ms',
      width: Dimensions.get('window').width > 640 ? '90@ms' : undefined,
      flexDirection: Dimensions.get('window').width > 640 ? 'column' : 'row',
      padding: Dimensions.get('window').width > 640 ? '15@ms' : '10@ms',
      alignItems: 'center',
    },
    sourceIcon: {
      marginTop: Dimensions.get('window').width > 640 ? '10@ms' : undefined,
      marginBottom: Dimensions.get('window').width > 640 ? '20@ms' : undefined,
      marginRight: Dimensions.get('window').width > 640 ? undefined : '10@ms',
    },
    sourceText: {
      fontSize: '10@ms',
      textAlign: 'center',
      fontWeight: '700',
      color: theme[800],
    },
    column: {
      justifyContent: 'center',
    },
    listVerticalSeparator: {
      height: Dimensions.get('window').width > 640 ? '20@mvs' : '10@mvs',
    },
  })
}
